import React, { useState, useEffect, useContext } from "react";
import intl from "react-intl-universal";
import Push from "push.js";
import { mdiAccount, mdiAccountGroup, mdiPlusBoxOutline, mdiUpload } from "@mdi/js";
import { useLocation } from "wouter";

import notification from "../../asset/notification.png";

import entryRest from "../../rest/entryRest";
import { UserContext } from "../contexts/UserContext";
import { useSse } from "../hooks/useSse";

import { Table } from "../components/Table";
import { Spacer } from "../components/Spacer";
import { Search } from "../components/Search";
import { Button } from "../components/Button";
import { Layout } from "../containers/Layout";
import { EntryItem } from "./entries/EntryItem";
import { useDebounce } from "../hooks/useDebounce";
import { UploadDialog } from "./entry/UploadDialog";
import { LazyScroll } from "../components/LazyScroll";
import { ContentBody } from "../containers/ContentBody";
import { ContentHeader } from "../containers/ContentHeader";
import { ValidationProvider } from "../contexts/ValidationContext";

export const Entries = () => {

    const userContext = useContext(UserContext);

    const [, setLocation] = useLocation();

    const [from, setFrom] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [showOnlyMyEntries, setShowOnlyMyEntries] = useState(localStorage.getItem("showOnlyMyEntries") === "true");
    const [searchQuery, setSearchQuery] = useState(localStorage.getItem("entrySearch") || "");
    const [loading, setLoading] = useState(true);

    const [showUploadDialog, setShowUploadDialog] = useState(false);

    const [entries, setEntries] = useState([]);

    useSse("ENTRY_CALCULATIONS_COMPLETE", entry => {
        Push.create("WWCluster", {
            body: intl.get("notification.calculations.complete", { entryName: entry.name }),
            icon: notification,
            onClick: function () {
                window.focus();
                this.close();
            }
        });
    });

    useSse("ENTRY_CALCULATION_FAILURE", entry => {
        Push.create("WWCluster", {
            body: intl.get("notification.calculations.failure", { entryName: entry.name }),
            icon: notification,
            onClick: function () {
                window.focus();
                this.close();
            }
        });
    });

    useSse("UPDATE_ENTRY", entry => {
        const result = entries.map(e => (e.id === entry.id ? entry : e));
        setEntries(result);
    });

    useSse("UPDATE_ENTRY_STATE", state => {
        const result = entries.map(e => (e.id === state.id ? { ...e, progress: state.progress, queueOrder: state.queueOrder } : e));
        setEntries(result);
    });

    useSse("INSERT_ENTRY", entry => {
        const result = [...entries];
        result.unshift(entry);
        setEntries(result);
    });

    useSse("DELETE_ENTRY", entryId => {
        const result = entries.filter(e => e.id !== entryId);
        setEntries(result);
    });

    const onLoadMore = async (entries, from, pageSize, searchQuery, showOnlyMyEntries) => {
        const recs = await entryRest.getLazy(from, pageSize, searchQuery, showOnlyMyEntries ? "MY" : "COMPANY");
        if (recs === -1) {
            // something went wrong, for know we ignore this.
            return;
        }
        setFrom(recs.nextFrom ?? 0);
        setEntries([...entries, ...recs.list]);
        setHasMore(recs.hasMore);
        setLoading(false);
    };

    const onLoad = async (pageSize, searchQuery, showOnlyMyEntries) => {
        setEntries([]);
        setFrom(0);
        await onLoadMore([], 0, pageSize, searchQuery, showOnlyMyEntries);
    };

    const onSearch = useDebounce((pageSize, searchQuery, showOnlyMyEntries) => {
        setLoading(true);
        onLoad(pageSize, searchQuery, showOnlyMyEntries);
    }, 500);

    useEffect(() => {
        Push.Permission.request();
        onSearch(60, searchQuery, showOnlyMyEntries);
        // When many data are loaded and scroll is at the bottom, then the LazyScroll gets mad and loads
        // next page after next page, never stopping.
        // Reset scroll to the top fixes this issue.
        const content = document.getElementById("contentBody");
        if (content) {
            content.scrollTop = 0;
        }
    }, [searchQuery, showOnlyMyEntries]);

    const onCreateEntry = () => setLocation("/entry/null/radios");

    const onCreateMultiEntries = () => setLocation("/multiEntries/null");

    return (
        <Layout>
            <ContentHeader title={intl.get("navigation.entries")}>
                <Button
                    svgIcon={mdiUpload}
                    value={intl.get("common.upload")}
                    onClick={() => setShowUploadDialog(true)} />
                <Spacer variant="vertical" />
                <Button
                    svgIcon={mdiPlusBoxOutline}
                    value={intl.get("common.create")}
                    onClick={onCreateEntry} />
                <Spacer variant="vertical" />
                <Button
                    svgIcon={mdiPlusBoxOutline}
                    value={intl.get("entries.createMulti")}
                    onClick={onCreateMultiEntries} />
                <Spacer variant="vertical" />
                <Button
                    svgIcon={showOnlyMyEntries ? mdiAccount : mdiAccountGroup}
                    value={intl.get(showOnlyMyEntries ? "entry.myEntries" : "entry.allEntries")}
                    onClick={() => {
                        const newValue = !showOnlyMyEntries;
                        localStorage.setItem("showOnlyMyEntries", newValue ? "true" : "false");
                        setShowOnlyMyEntries(newValue);
                    }}
                />
                <Spacer variant="vertical" />
                <Search
                    id="entrySearch"
                    value={searchQuery}
                    onChange={newVal => setSearchQuery(newVal)} />
            </ContentHeader>
            <ContentBody>
                <LazyScroll
                    id="entriesTable"
                    hasMore={hasMore}
                    loadMore={() => onLoadMore(entries, from, 60, searchQuery, showOnlyMyEntries)}
                    loading={loading}
                    text={intl.get("common.loadingText")}>
                    <Table id="entryTable">
                        <thead>
                            <tr>
                                <th width="10" />
                                <th width="80">{intl.get("entries.siteNum")}</th>
                                <th width="300">{intl.get("entries.name")}</th>
                                <th width="170">{intl.get("entries.owner")}</th>
                                <th width="170">{intl.get("entries.calculatedAt")}</th>
                                <th width="130">{intl.get("entries.progress")}</th>
                                <th width="300">{intl.get("entries.fieldType")}</th>
                                <th width="115">{intl.get("entries.spatialArea")}</th>
                                <th width="150">{intl.get("entries.environmentFactor")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(entries) && entries?.map(entry =>
                                <EntryItem key={`${entry.user?.username}-${entry.id}`} entry={entry} user={userContext.user} />
                            )}
                        </tbody>
                    </Table>
                </LazyScroll>
            </ContentBody>
            {showUploadDialog &&
                        <ValidationProvider>
                            <UploadDialog
                                onClose={() => setShowUploadDialog(false)} />
                        </ValidationProvider>
            }
        </Layout>
    );
};